export default {
    nav: {
        home: 'Home',
        about: 'About',
        certifications: 'Certifications',
        portfolio: 'Portfolio',
        services: 'Services',
        contact: 'Contact',
        language: {
            en: 'English',
            ar: 'Arabic',
            tr: 'Turkish',
        },
    },
    hero: {
        kicker: 'Code Dragon Portfolio',
        title: 'My name is Mohammed. I am a web developer, and here I do not just build you a website. I give you a unique experience.',
        subtitle:
            'I turn ideas into responsive, interactive interfaces that pair clean design, strong performance, and comfortable user journeys.',
        cta: 'See My Work',
        secondaryCta: 'Contact Me',
        badges: {
            javascript: 'JavaScript',
            responsive: 'Responsive UI',
            performance: 'Performance',
        },
    },
    about: {
        title: 'About Me',
        paragraph1:
            'I am Muhammed Garib Al-Ahmad, a Computer Programming student at Anadolu University and a certified Web Developer. I specialize in building modern, high-performance web interfaces using HTML, CSS, JavaScript, and Bootstrap.',
        paragraph2:
            'By leveraging tools like Vite and the latest design trends, I create fast and easy-to-use websites at Code Dragon. My goal is to transform ideas into seamless digital experiences where clean code meets exceptional design.',
        code: `const frontendDev = {
  name: "Muhammed Garib Al-Ahmad",
  role: "Front-End Developer",
  skills: ["HTML/CSS", "JavaScript", "React"],
  focus: ["Performance", "Accessibility", "UX"]
}`,
    },
    skills: {
        html: 'HTML5 & CSS3',
        js: 'JavaScript / ES6+',
        react: 'React',
        sass: 'SASS/SCSS',
        bootstrap: 'Bootstrap',
        tailwind: 'Tailwind CSS',
        vite: 'Vite & Webpack',
        git: 'Git & GitHub',
        responsive: 'Responsive Design',
    },
    certifications: {
        title: 'Certifications & Achievements',
        subtitle: 'Professional certifications and continuous learning in web development',
        responsive: {
            title: 'Responsive Web Design',
            issuer: 'Udemy',
            date: '2023',
            description:
                'Certification in building responsive websites using HTML5, CSS3, and mobile-first design principles.',
            skills: {
                html: 'HTML5',
                css: 'CSS3',
                mobile: 'Mobile-First',
            },
        },
        ai: {
            title: 'AI for Everyone',
            issuer: 'Google / Coursera',
            date: '2026',
            description:
                'Leveraging artificial intelligence tools and techniques to enhance productivity and streamline development workflows.',
            skills: {
                tools: 'AI Tools',
                prompt: 'Prompt Engineering',
            },
        },
        webInterface: {
            title: 'Web Interface Dev',
            issuer: 'Hsoub Academy',
            date: '2026',
            description:
                'Professional web interface development certification, demonstrating expertise in crafting high-performance, responsive, and modern user interfaces.',
            skills: {
                web: 'Web Development',
                responsive: 'Responsive Design',
                performance: 'Performance',
            },
        },
    },
    portfolio: {
        title: 'Featured Projects',
        projectPizza: {
            title: 'Pizza Restaurant Project',
            description:
                'Modern, fully-responsive restaurant website built with Vanilla JavaScript and optimized using Webpack for superior performance. Features interactive menu, shopping cart, and customer reviews.',
            live: 'Live Demo',
            github: 'GitHub',
        },
        projectMajalis: {
            title: 'Majalis Al-Warraqyn',
            description:
                'A modern Arabic landing page with full RTL support, neon-inspired visuals, smooth animations, and interactive alerts for a fast, engaging user experience.',
            live: 'Live Demo',
        },
        modal: {
            fallbackDescription: 'Project details coming soon.',
            featuresTitle: 'Key Features',
            techTitle: 'Technologies',
            close: 'Close',
            projects: {
                ecommerce: {
                    title: 'E-Commerce Platform',
                    description:
                        'A full-featured e-commerce solution with real-time inventory management, shopping cart, and secure payment processing using Stripe.',
                    features: [
                        'Real-time inventory management',
                        'Shopping cart with persistent storage',
                        'Secure payment processing',
                        'User authentication and profiles',
                        'Order tracking system',
                        'Admin dashboard',
                    ],
                },
                analytics: {
                    title: 'Analytics Dashboard',
                    description:
                        'Interactive data visualization dashboard providing real-time metrics, advanced filtering, and export capabilities for business intelligence.',
                    features: [
                        'Real-time data visualization',
                        'Advanced filtering and search',
                        'Customizable dashboards',
                        'Export to CSV/PDF',
                        'Multiple chart types',
                        'Performance metrics',
                    ],
                },
                tasks: {
                    title: 'Task Management App',
                    description:
                        'Collaborative task management tool with real-time updates, team collaboration features, and intuitive drag-and-drop interface.',
                    features: [
                        'Create and manage tasks',
                        'Drag-and-drop interface',
                        'Team collaboration',
                        'Real-time updates',
                        'Due date reminders',
                        'Priority levels',
                    ],
                },
            },
        },
    },
    features: {
        title: 'What I Offer',
        subtitle: 'Professional front-end development services to bring your web projects to life',
        items: {
            responsive: {
                title: 'Responsive Web Development',
                description:
                    'Building fully responsive websites that work seamlessly across all devices and screen sizes.',
                list: [
                    'Mobile-first approach',
                    'Cross-browser compatibility',
                    'Flexible layouts & grids',
                    'Touch-friendly interfaces',
                ],
            },
            modernJs: {
                title: 'Modern JavaScript Development',
                description:
                    'Creating interactive and dynamic web applications using the latest JavaScript frameworks.',
                list: [
                    'React & Vue.js applications',
                    'ES6+ best practices',
                    'State management solutions',
                    'API integration',
                ],
            },
            ui: {
                title: 'UI Implementation',
                description:
                    'Converting designs and mockups into pixel-perfect, functional web interfaces.',
                list: [
                    'Figma/XD to code',
                    'CSS animations & transitions',
                    'Component-based architecture',
                    'Design system integration',
                ],
            },
            performance: {
                title: 'Performance Optimization',
                description:
                    'Ensuring your website loads fast and runs smoothly for the best user experience.',
                list: [
                    'Code splitting & lazy loading',
                    'Image optimization',
                    'Bundle size reduction',
                    'Performance monitoring',
                ],
            },
            accessibility: {
                title: 'Web Accessibility',
                description:
                    'Making your website accessible to all users, including those with disabilities.',
                list: [
                    'WCAG compliance',
                    'Screen reader support',
                    'Keyboard navigation',
                    'Semantic HTML structure',
                ],
            },
            maintenance: {
                title: 'Code Maintenance & Updates',
                description:
                    'Keeping your front-end codebase clean, updated, and easy to maintain.',
                list: ['Bug fixes & updates', 'Code refactoring', 'Documentation', 'Version control with Git'],
            },
        },
    },
    contact: {
        title: 'Get In Touch',
        namePlaceholder: 'Your Name',
        emailPlaceholder: 'Your Email',
        messagePlaceholder: 'Your Message',
        submit: 'Send Message',
        sending: 'Sending...',
    },
    footer: {
        rights: '© 2026 Code Dragon Portfolio. All rights reserved.',
    },
    alerts: {
        successTitle: 'Message Sent Successfully! ✅',
        successBody:
            'Thank you, <strong style="color: #c5a059;">{{name}}</strong>!<br>Your message has been sent successfully!',
        successConfirm: 'Great!',
        errorTitle: 'Error! ❌',
        errorText: 'Sorry, something went wrong. Please try again.',
        errorConfirm: 'OK',
        warningTitle: 'Warning! ⚠️',
        warningConfirm: 'OK',
        validation: {
            nameRequired: 'Please enter your name',
            nameShort: 'Name must be at least 2 characters long',
            emailRequired: 'Please enter your email',
            emailInvalid: 'Please enter a valid email address',
            messageRequired: 'Please enter your message',
            messageShort: 'Message must be at least 10 characters long',
        },
    },
};
